import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { unstable_batchedUpdates } from "react-dom"
import { FormProvider, useForm } from "react-hook-form"
import { store } from "react-notifications-component"
import { Route, useHistory, useRouteMatch } from "react-router"
import { notificationOptions } from "../../../api/Definitions"
import Loader from "../../../components/Loader/Loader"
import Seo from "../../../components/Seo/Seo"
import { iAccess } from "../../../definitions/Access"
import {
  iInspectorProps,
  iResponse,
  iUrlParams,
} from "../../../definitions/interfaces"
import { iNews } from "../../../definitions/News"
import { ItemWithAccess } from "../../../definitions/types"
import { errorNotification } from "../../../helpers/notificationFunctions"
import {
  get,
  handleDeleteData,
  handleUpdateData,
} from "../../../store/data/actions"
import NewsOverview from "./Overview"
import NewsHeader from "../Header"
import NewsDescription from "./Description"
import NewsUploads from "./Uploads"

const NewsInspector: FunctionComponent<iInspectorProps<iNews>> = ({
  item,
  access,
  reload,
}) => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iUrlParams>()
  const history = useHistory()

  const methods = useForm<ItemWithAccess<iNews>>({
    shouldUnregister: false,
    defaultValues: { ...item, __access: access },
  })

  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset({ ...item, __access: access })
  }, [item, access])

  const onSubmit = (data: ItemWithAccess<iNews>) => {
    setLoading(true)
    handleUpdateData({
      data,
      fields: dirtyFields as Partial<ItemWithAccess<iNews>>,
      link: "/v2/news",
    })
      .then(() => {
        setLoading(false)
        reload()
        store.addNotification({
          title: "Success!",
          message: "News successfully updated!",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        errorNotification(e)
        setLoading(false)
      })
  }

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/news/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "News successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as ItemWithAccess<iNews>)
    })()
  }
  return (
    <FormProvider {...methods}>
      <Seo title={`${item.title ? item.title : "Inspector"} | VR-All-Art`} />
      <NewsHeader
        submitDeleteText={"Delete"}
        access={access}
        title={item.title || "Inspector"}
        submitText="Update"
        isLoading={isLoading}
        handleSubmit={invokeSubmit}
        cover={item.poster}
        handleDelete={onDelete}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <div className="inspector">
          <form
            onSubmit={methods.handleSubmit((data) => {
              onSubmit(data as ItemWithAccess<iNews>)
            })}
            className="inspector-form"
          >
            <Route exact path={match.path}>
              <NewsOverview />
            </Route>
            <Route exact path={match.path + "/description"}>
              <NewsDescription />
            </Route>
            <Route exact path={match.path + "/manage-files"}>
              <NewsUploads item={item} />
            </Route>
            <button className="d-none" type="submit">
              Submit
            </button>
          </form>
        </div>
      )}
    </FormProvider>
  )
}

const InspectorLoader: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iNews>()
  const match = useRouteMatch<iUrlParams>()

  const load = () => {
    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { collection: "News", url: match.params.url },
      }),
      get<iResponse<iNews>>({ link: "/v2/news/" + match.params.url }),
    ]).then((result) => {
      const [accessResult, dataResult] = result
      unstable_batchedUpdates(() => {
        setItem(dataResult.data)
        setAccess(accessResult.data)
        setLoading(false)
      })
    })
  }

  useMemo(() => {
    load()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <NewsInspector item={item} access={access} reload={load} />
  ) : null
}

export default InspectorLoader
